import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TableRow from './TableRow';

/**
 * Table component
 * @param {boolean} hasTableHead - Determines if a header row will appear in the table
 * @param {boolean} hasSelector - Determines ia a selector column will appear at the left side of the table
 * @param {string} selectorType - Determines if the selector column will contain a checkbox or radio button
 * @param {string} tableStyles - CSS classes to add to the overall table
 * @param {array} tableHeadings - Array of objects containingthe table headings for the columns, the length
 *                                must match the number of columns in the table.
 *                                Contains objects with the keys:
 *                                isSortable {boolean} - Determines if a column is sortable
 *                                sortBy {string} - The parameter to be used for sorting, this should match
 *                                a key in the objects that will be in the contents of the table
 *                                display {*} - Whatever contents will be displayed in the table cell
 * @param {array} tableData - An array of objects containing the data to be displayed in the table.
 *                            Contains objects with the keys:
 *                            sortBy {string} - A string or number to be used for sorting
 *                            display {*} - Whatever contents will be displayed in the table cell
 * @param {string} sortParam - The initial parameter to sort based on
 * @param {string} headerStyles - CSS classes to be added to each header cell
 * @param {string} selectorName - The value that will be used by the selector as the name of that selector.
 * @param {string} selectorParamValue - The parameter that will be used by the selector as the value of that selector. This value will be what is returned in the form
 */

function Table(props) {
  const {
    tableData,
    hasSelector,
    selectorParam,
    selectorName,
    selectorType,
    sortParam,
    tableStyles,
    tableHeadings,
    hasTableHead,
    headerStyles,
    'data-test': dataTest,
  } = props;

  const [tableSort, setTableSort] = useState({
    sortParam: sortParam,
    sortOrder: 'az',
  });

  /**
   * Function to set the sort parameter in local tableSort
   * @param {string} param - The sort parameter to sort the table by
   */

  function setSortType(param) {
    const value = param;
    let sortOrder = tableSort.sortOrder;
    if (value === tableSort.sortParam) {
      if (tableSort.sortOrder === 'az') {
        sortOrder = 'za';
      } else {
        sortOrder = 'az';
      }
    }
    setTableSort({
      ...tableSort,
      sortParam: value,
      sortOrder: sortOrder,
    });
  }

  /**
   * Function to sort the table data by the sort parameter and in order by the sort order in local tableSort
   * @param {array} data - the data to be sorted
   * @returns {array} - the sorted data
   */
  function dataSort(data) {
    function checkIfLetters(value) {
      const regex = RegExp('^d');
      return regex.test(value);
    }
    let sortedData;
    const sortParam = tableSort.sortParam;

    if (tableSort.sortOrder === 'az') {
      sortedData = data.sort(function (a, b) {
        let aParam = a[sortParam].sortBy;
        let bParam = b[sortParam].sortBy;
        if (typeof aParam === 'string') {
          aParam = aParam.trim();
        }
        if (typeof bParam === 'string') {
          bParam = bParam.trim();
        }
        const aIsLetters = checkIfLetters(aParam);
        const bIsLetters = checkIfLetters(bParam);
        if (typeof aParam === 'string' && aIsLetters && bIsLetters) {
          aParam = aParam.toUpperCase(); // ignore upper and lowercase
          bParam = bParam.toUpperCase(); // ignore upper and lowercase
        }

        if (aParam < bParam) {
          return -1;
        }
        if (aParam > bParam) {
          return 1;
        }
        // names must be equal
        return 0;
      });
    } else if (tableSort.sortOrder === 'za') {
      sortedData = data.sort(function (a, b) {
        let aParam = a[sortParam].sortBy;
        let bParam = b[sortParam].sortBy;
        if (typeof aParam === 'string') {
          aParam = aParam.trim();
        }
        if (typeof bParam === 'string') {
          bParam = bParam.trim();
        }
        const aIsLetters = checkIfLetters(aParam);
        const bIsLetters = checkIfLetters(bParam);
        if (typeof aParam === 'string' && aIsLetters && bIsLetters) {
          aParam = aParam.toUpperCase(); // ignore upper and lowercase
          bParam = bParam.toUpperCase(); // ignore upper and lowercase
        }

        if (aParam < bParam) {
          return 1;
        }
        if (aParam > bParam) {
          return -1;
        }
        // names must be equal
        return 0;
      });
    }
    return sortedData;
  }

  /**
   * Function to create the rows of the table
   * @param {array} data - The data to be split into rows
   * @returns {array} The array of TableRow components to be displayed
   */
  function createTableRows() {
    const data = tableData;
    const tableDataArr = dataSort(data);
    const hasSelectorBool = hasSelector;
    const tableRows = tableDataArr.map((row, index) => {
      let isChecked;
      if (hasSelectorBool && row[selectorParam]) {
      }
      return (
        <TableRow
          setTableHeadSort={setSortType}
          tableSortParam={tableSort.sortParam}
          tableSortOrder={tableSort.sortOrder}
          hasSelector={hasSelectorBool}
          data={row}
          key={index}
          index={index}
          data-test="table-row"
          isChecked={isChecked}
          selectorName={selectorName}
          selectorType={selectorType}
        />
      );
    });
    return tableRows;
  }

  /**
   * Function to create the heading row for the table
   * @param {array} headings - An array of data to be used for the column headings
   * @returns {object} The table row component to be displayed at the top of the table
   */
  function createTableHeadings(headings) {
    let tableHead;
    if (hasTableHead) {
      tableHead = (
        <TableRow
          isTableHead={hasTableHead}
          setTableHeadSort={setSortType}
          tableSortParam={tableSort.sortParam}
          tableSortOrder={tableSort.sortOrder}
          hasSelector={hasSelector}
          data={headings}
          data-test="table-head"
          headerStyles={headerStyles}
          selectorName={selectorName}
          selectorType={selectorType}
        />
      );
    }

    return tableHead;
  }

  const selectorStyle = hasSelector ? ' table__checkbox' : '';

  return (
    <div role="table" className={'table ' + tableStyles + selectorStyle}
    data-test={dataTest}>
      {createTableHeadings(tableHeadings)}
      {createTableRows()}
    </div>
  );
}
TableRow.propTypes = {
  hasTableHead: PropTypes.bool,
  tableData: PropTypes.array,
  tableHeadings: PropTypes.object,
  hasSelector: PropTypes.bool,
  tableStyles: PropTypes.string,
};

export default Table;
